import { captureSentryException } from '@/utils/sentry';
import { useOrgStore } from '@/stores/org';
import { isExplainedOnScreen } from '@/utils/handledServerErrors';

export default class {
    constructor(error) {
        this.error = error;
    }

    /**
     * Why the server refused, in the reader's own language.
     *
     * A refusal travels with a code because an API response is translated in
     * the application's locale whoever asks — SetLocale reads the session and
     * runs on the web group only. A sentence written here reaches the person
     * who is reading it; the server's own message stays as the fallback for
     * every refusal that has no code of its own.
     */
    refusalReason() {
        const reasons = {
            bundle_exhausted: i18n.t(
                'There is no signature left in your prepaid bundle. Buy more to carry on sending.'
            ),
            billing_overdue: i18n.t('An invoice from Elixio is past due. Pay it from the Billing page to carry on.'),
            stripe_not_connected: i18n.t(
                'Online payment is not set up: connect a Stripe account in Settings, Payments.'
            )
        };

        return reasons[this.error.data?.code] || this.error.data?.message;
    }

    manage() {
        // No response at all: the request never reached the server (offline, DNS,
        // TLS, timeout, aborted). Staying silent here reads as "nothing happened"
        // while the save did not go through — always say so.
        if (!this.error) {
            Modal.alert(i18n.t('Error'), i18n.t('An error occurred. Please try again.'));

            return;
        }

        // Suspended for non-payment: the interceptor already took the person to
        // the page that explains it — a dialog on top would say it twice.
        if (this.error.data?.error === 'organization_suspended') {
            return;
        }

        // The loss window was closed without confirming (see bootstrap.js): the
        // person chose to keep the deal where it was, which is not an error.
        if (this.error.data?.loss_dismissed) {
            return;
        }

        // The server marked this response as a question rather than a failure:
        // it hands the decision back to the caller, which shows its own dialog.
        // Announcing an "Error" first would contradict the confirmation that
        // follows a fraction of a second later.
        if (this.error.data?.confirmable) {
            return;
        }

        // The server refused the action and said why — a verdict, not a
        // malformed request. Showing it under "Validation error" would be
        // false: nothing about the request was invalid, it simply was not
        // allowed. The reason is written for the person reading it.
        if (this.error.data?.refused) {
            // Out of units: the refusal says where more come from, so it takes
            // whoever may pay the organization's bills there.
            if (this.error.data.code === 'bundle_exhausted' && useOrgStore().can('settings.billing')) {
                Modal.confirm(i18n.t('Not sent'), this.refusalReason(), {
                    confirmText: i18n.t('See my subscription')
                }).then(
                    confirmed => confirmed && window.router.push({ name: 'settings-billing-subscription' }),
                    () => {}
                );

                return;
            }

            Modal.alert(i18n.t('Not sent'), this.refusalReason());

            return;
        }

        // The screen already explains this one, with the action to take (see
        // handledServerErrors.js). Handled here rather than at each call site so
        // the list, the open message, the attachments and whatever comes next all
        // behave the same — the first attempt at this fix patched only the list,
        // and the modal came straight back on opening a message.
        if (isExplainedOnScreen(this.error)) {
            return;
        }

        switch (this.error.status) {
            // 401 (unauthenticated — session/token gone) is handled centrally
            // by the axios interceptor in bootstrap.js, which transparently
            // refreshes the token and, if needed, redirects to login. Listed
            // explicitly so the default below never double-reports it.
            case 401:
                break;

            case 403:
                Modal.alert(i18n.t('Unauthorized'), i18n.t("You don't have access to the ressource."));
                break;

            case 404:
                Modal.alert(i18n.t('Not found'), i18n.t('The item was not found.'));
                break;

            case 419:
                if (window.location.pathname === '/login') {
                    window.location.reload();
                } else {
                    Modal.alert(
                        i18n.t('Warning'),
                        i18n.t('The verification token expired, we need to refresh the page.'),
                        () => {
                            window.location.href = window.location;
                        }
                    );
                }
                break;

            // Rate limited. statusText is empty over HTTP/2, which left the
            // dialog with a title and nothing else — the person had no idea
            // what had happened or what to do.
            case 429: {
                // Laravel's own body is an untranslated "Too Many Attempts.",
                // so the wording comes from here either way.
                const retryAfter = Number(this.error.headers?.['retry-after']);

                Modal.alert(
                    i18n.t('Warning'),
                    retryAfter > 0
                        ? i18n.t('Too many attempts. Try again in {seconds} seconds.', { seconds: retryAfter })
                        : i18n.t('Too many attempts. Wait a moment and try again.')
                );
                break;
            }

            case 422: {
                const errors = this.error.data?.errors;
                // Server messages are stored in English; translate them here so a
                // French UI shows French. A message without a translation key falls
                // through i18n.t unchanged, so this never blanks an unknown one.
                let message = this.error.data?.message
                    ? i18n.t(this.error.data.message)
                    : i18n.t('The form contains errors.');
                if (errors) {
                    const messages = Object.values(errors)
                        .flat()
                        .map(m => i18n.t(m));
                    if (messages.length) {
                        message = messages.join('<br>');
                    }
                }
                Modal.alert(i18n.t('Validation error'), message);
                break;
            }

            case 500:
                captureSentryException(this.error);
                Modal.alert(
                    i18n.t('Internal Server Error'),
                    `
                            <p>` +
                        i18n.t('The server encountered an internal error and was unable to complete your request.') +
                        `</p>
                            <p>` +
                        i18n.t('Try again, if the problem persist contact the support with the following error :') +
                        `</p>
                            <p>${this.error.data?.message ?? ''}</p>
                    `
                );
                break;

            // Every other status (400, 409, 413, 502, 503, 504…) still means the
            // action failed. Reporting nothing would leave the user believing it
            // went through.
            default:
                Modal.alert(
                    i18n.t('Error'),
                    (this.error.data?.message && i18n.t(this.error.data.message)) ||
                        this.error.statusText ||
                        i18n.t('An error occurred. Please try again.')
                );
        }
    }
}
